if (!customElements.get('paw-stable-banner-slider')) {
  customElements.define('paw-stable-banner-slider', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const track = this.querySelector('[data-slim-track]');
      const slides = track ? [...track.querySelectorAll('[data-slim-slide]')] : [];
      if (slides.length < 3) return;
      this.controller = new AbortController();
      const {signal} = this.controller;
      const count = slides.length;
      const status = this.querySelector('[data-slim-status]');
      const controls = this.querySelector('[data-slim-controls]');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let position = 0;
      let destination = 0;
      let pitch = 1;
      let direction = 1;
      let frame = 0;
      let wheelTimer;
      let wheelOrigin = null;
      let wheelActive = false;
      let wheelLastDelta = 0;
      let pointer = null;
      let suppressClickUntil = 0;

      // Mobile Touch Tracking (iOS Safari & Android)
      let isTouching = false;
      let touchStartX = 0;
      let touchStartY = 0;
      let touchStartTime = 0;
      let touchOrigin = 0;
      let isHorizontalDrag = null;
      let lastTouchX = 0;

      const modulo = value => ((value % count) + count) % count;

      const render = () => {
        slides.forEach((slide, index) => {
          const offset = modulo(index - position + count / 2) - count / 2;
          slide.style.transform = `translate3d(${(offset * pitch * direction).toFixed(3)}px,0,0)`;
          // Cull off-screen slides to prevent WebKit tile invalidation and GPU composite flicker
          const isVisible = Math.abs(offset) <= 1.35;
          if (isVisible) {
            if (slide.style.visibility === 'hidden') slide.style.visibility = '';
            if (slide.hasAttribute('aria-hidden')) slide.removeAttribute('aria-hidden');
          } else {
            if (slide.style.visibility !== 'hidden') slide.style.visibility = 'hidden';
            if (!slide.hasAttribute('aria-hidden')) slide.setAttribute('aria-hidden', 'true');
          }
        });
      };

      const update = announce => {
        const current = modulo(Math.round(position));
        track.dataset.slimIndex = String(current);
        if (announce && status) status.textContent = slides[current].getAttribute('aria-label');
      };

      const stop = () => {
        cancelAnimationFrame(frame);
        frame = 0;
      };

      const go = (next, animate = true, announce = true) => {
        stop();
        clearTimeout(wheelTimer);
        wheelOrigin = null;
        destination = next;
        const start = position;
        if (!animate || reducedMotion.matches || Math.abs(next - start) < .001) {
          position = destination = modulo(next);
          render();
          update(announce);
          return;
        }
        const started = performance.now();
        const duration = Math.min(380, Math.max(260, Math.abs(next - start) * 280));
        const step = now => {
          const elapsed = Math.min(1, (now - started) / duration);
          position = start + (next - start) * (1 - Math.pow(1 - elapsed, 3));
          render();
          if (elapsed < 1) frame = requestAnimationFrame(step);
          else {
            frame = 0;
            position = destination = modulo(next);
            render();
            update(announce);
          }
        };
        frame = requestAnimationFrame(step);
      };

      const select = (index, animate = true) => {
        const current = Math.round(position);
        const distance = modulo(index - modulo(current) + count / 2) - count / 2;
        go(current + distance, animate);
      };

      const settleGesture = (origin, velocity = 0) => {
        let next = Math.round(position);
        const distance = position - origin;
        const flickThreshold = 0.22; // px per ms
        if (Math.abs(velocity) > flickThreshold) {
          next = Math.round(origin) - Math.sign(velocity) * direction;
        } else if (next === Math.round(origin) && Math.abs(distance) * pitch > Math.min(30, pitch * 0.1)) {
          next += Math.sign(distance);
        }
        go(next);
      };

      track.setAttribute('data-slim-loop', '');

      const measure = () => {
        const style = getComputedStyle(track);
        direction = style.direction === 'rtl' ? -1 : 1;
        const gapVal = parseFloat(style.columnGap) || parseFloat(style.gap) || (window.innerWidth < 750 ? 10 : 14);
        pitch = (slides[0]?.getBoundingClientRect().width || track.clientWidth * 0.8) + gapVal;
        render();
      };
      measure();
      update(false);

      if (controls) controls.hidden = false;

      // Controls and keyboard
      this.querySelector('[data-slim-prev]')?.addEventListener('click', () => go((frame ? destination : Math.round(position)) - 1), {signal});
      this.querySelector('[data-slim-next]')?.addEventListener('click', () => go((frame ? destination : Math.round(position)) + 1), {signal});

      track.addEventListener('keydown', event => {
        if (event.target !== track) return;
        const current = frame ? destination : Math.round(position);
        if (event.key === 'ArrowLeft') go(current - direction);
        else if (event.key === 'ArrowRight') go(current + direction);
        else if (event.key === 'Home') select(0);
        else if (event.key === 'End') select(count - 1);
        else return;
        event.preventDefault();
      }, {signal});

      track.addEventListener('focusin', event => {
        if (pointer || isTouching) return;
        const index = slides.indexOf(event.target.closest('[data-slim-slide]'));
        if (index >= 0) select(index, false);
      }, {signal});

      this.addEventListener('shopify:block:select', event => {
        const index = slides.indexOf(event.target.closest('[data-slim-slide]'));
        if (index >= 0) select(index, false);
      }, {signal});

      // --- iOS Safari & Mobile Touch Handler ---
      const onTouchStart = event => {
        if (event.touches.length !== 1) return;
        stop();
        clearTimeout(wheelTimer);
        wheelOrigin = null;
        const touch = event.touches[0];
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
        lastTouchX = touch.clientX;
        touchStartTime = performance.now();
        touchOrigin = position;
        isHorizontalDrag = null;
        isTouching = true;
      };

      const onTouchMove = event => {
        if (!isTouching || event.touches.length !== 1) return;
        const touch = event.touches[0];
        const dx = touch.clientX - touchStartX;
        const dy = touch.clientY - touchStartY;

        if (isHorizontalDrag === null) {
          const absX = Math.abs(dx);
          const absY = Math.abs(dy);
          if (absX > 6 || absY > 6) {
            isHorizontalDrag = absX >= absY;
          }
        }

        if (isHorizontalDrag === true) {
          if (event.cancelable) event.preventDefault();
          lastTouchX = touch.clientX;
          position = touchOrigin - (dx / pitch) * direction;
          render();
        }
      };

      const onTouchEnd = event => {
        if (!isTouching) return;
        isTouching = false;
        if (isHorizontalDrag === true) {
          suppressClickUntil = performance.now() + 400;
          const elapsed = Math.max(1, performance.now() - touchStartTime);
          const endX = event.changedTouches?.[0]?.clientX ?? lastTouchX;
          const dx = endX - touchStartX;
          const velocity = dx / elapsed;
          settleGesture(touchOrigin, velocity);
        }
        isHorizontalDrag = null;
      };

      const onTouchCancel = () => {
        if (isTouching && isHorizontalDrag === true) {
          settleGesture(touchOrigin);
        }
        isTouching = false;
        isHorizontalDrag = null;
      };

      track.addEventListener('touchstart', onTouchStart, {signal, passive: true});
      track.addEventListener('touchmove', onTouchMove, {signal, passive: false});
      track.addEventListener('touchend', onTouchEnd, {signal, passive: true});
      track.addEventListener('touchcancel', onTouchCancel, {signal, passive: true});

      // --- Desktop Mouse Pointer Handler (Non-touch) ---
      track.addEventListener('pointerdown', event => {
        if (event.pointerType === 'touch') return;
        if (!event.isPrimary || event.button !== 0) return;
        stop();
        clearTimeout(wheelTimer);
        wheelOrigin = null;
        pointer = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          origin: position,
          dragging: false,
          time: performance.now()
        };
      }, {signal});

      track.addEventListener('pointermove', event => {
        if (!pointer || event.pointerId !== pointer.id) return;
        const dx = event.clientX - pointer.x;
        const dy = event.clientY - pointer.y;
        if (!pointer.dragging) {
          if (Math.abs(dx) < 6 || Math.abs(dx) <= Math.abs(dy)) return;
          pointer.dragging = true;
          try { track.setPointerCapture(event.pointerId); } catch (_) {}
        }
        position = pointer.origin - (dx / pitch) * direction;
        render();
      }, {signal});

      const onPointerRelease = event => {
        if (!pointer || event.pointerId !== pointer.id) return;
        const finished = pointer;
        pointer = null;
        try {
          if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
        } catch (_) {}
        if (finished.dragging) {
          suppressClickUntil = performance.now() + 400;
          const elapsed = Math.max(1, performance.now() - finished.time);
          const vx = (event.clientX - finished.x) / elapsed;
          settleGesture(finished.origin, vx);
        } else if (Math.abs(position - Math.round(position)) > 0.001) {
          settleGesture(finished.origin);
        }
      };

      track.addEventListener('pointerup', onPointerRelease, {signal});
      track.addEventListener('pointercancel', onPointerRelease, {signal});
      track.addEventListener('lostpointercapture', onPointerRelease, {signal});
      track.addEventListener('pointerleave', event => {
        if (pointer && !pointer.dragging) onPointerRelease(event);
      }, {signal});

      track.addEventListener('click', event => {
        if (performance.now() < suppressClickUntil) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, {signal, capture: true});

      track.addEventListener('dragstart', event => event.preventDefault(), {signal});

      // --- macOS Trackpad Smooth Wheel Handler ---
      track.addEventListener('wheel', event => {
        if (event.ctrlKey || pointer || isTouching) return;
        const absX = Math.abs(event.deltaX);
        const absY = Math.abs(event.deltaY);

        if (!wheelActive && (absX < 4 || absX <= absY)) return;

        event.preventDefault();
        stop();
        wheelActive = true;
        if (wheelOrigin === null) wheelOrigin = position;

        const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? track.clientWidth : 1;
        const delta = event.deltaX * unit;
        wheelLastDelta = delta;

        position += (delta / pitch) * direction * 0.75;
        render();

        clearTimeout(wheelTimer);
        wheelTimer = setTimeout(() => {
          wheelActive = false;
          const origin = wheelOrigin;
          wheelOrigin = null;
          settleGesture(origin !== null ? origin : position, -wheelLastDelta * 0.04);
        }, 160);
      }, {signal, passive: false});

      this.observer = new ResizeObserver(measure);
      this.observer.observe(track);

      this.cleanup = () => {
        stop();
        clearTimeout(wheelTimer);
        track.removeAttribute('data-slim-loop');
        delete track.dataset.slimIndex;
        slides.forEach(slide => {
          slide.style.removeProperty('transform');
          slide.style.removeProperty('visibility');
          slide.removeAttribute('aria-hidden');
        });
        if (controls) controls.hidden = true;
      };
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.cleanup?.();
      this.controller = null;
    }
  });
}
